import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { useNavigate } from 'react-router-dom';
import { 
  Shield,
  Car,
  Settings,
  Wrench,
  ArrowRight,
  Star
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProductOverview = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const iconMap = {
    Shield: Shield,
    Car: Car,
    Settings: Settings,
    Tool: Wrench,
    Wrench: Wrench
  };

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch(`${API}/products/categories`);
        const result = await response.json();
        if (result.success) {
          setCategories(result.data);
        }
      } catch (error) {
        console.error('Error fetching categories:', error);
        // Fallback to static data if API fails
        setCategories([
          {
            category_id: "all-weather",
            name: "All-Weather Mats",
            description: "Heavy-duty protection against mud, water, and snow",
            icon: "Shield",
            product_count: 4
          },
          {
            category_id: "carpet",
            name: "Carpet Mats",
            description: "Premium carpeted mats with superior comfort and style",
            icon: "Car",
            product_count: 3
          },
          {
            category_id: "custom",
            name: "Custom Solutions",
            description: "Tailored solutions for specific vehicle makes and models",
            icon: "Settings",
            product_count: 8
          },
          {
            category_id: "specialty",
            name: "Specialty Mats",
            description: "Utility and specialty applications for various needs",
            icon: "Tool",
            product_count: 2
          }
        ]);
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, []);

  const handleViewProducts = (categoryId, categoryName) => {
    // Navigate to products page with category filter
    navigate(`/products?category=${encodeURIComponent(categoryId || categoryName)}`);
  };

  if (loading) {
    return (
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="animate-pulse">
              <div className="h-8 bg-gray-200 rounded w-64 mx-auto mb-4"></div>
              <div className="h-4 bg-gray-200 rounded w-96 mx-auto"></div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <Badge className="bg-automats-red/10 text-automats-red border-automats-red/20 mb-4">
            Product Explorer
          </Badge>
          <h2 className="text-4xl font-bold text-gray-900 mb-6">
            Premium Mats for Every Application
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            From all-weather protection to custom solutions, discover our complete range of 
            100% recycled automotive floor mats designed for performance and sustainability.
          </p>
        </div>

        {/* Product Categories Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
          {categories.map((category, index) => {
            const IconComponent = iconMap[category.icon] || Shield; // Fallback to Shield if icon not found
            return (
              <Card 
                key={index} 
                className="group hover:shadow-xl transition-all duration-300 border-2 hover:border-automats-red/20 cursor-pointer"
              >
                <CardHeader className="text-center pb-4">
                  <div className="mb-4">
                    <div className="inline-flex p-4 rounded-full bg-automats-red/10 group-hover:bg-automats-red/20 transition-colors">
                      <IconComponent className="h-8 w-8 text-automats-red" />
                    </div>
                  </div>
                  <CardTitle className="text-xl text-gray-900 group-hover:text-automats-red transition-colors">
                    {category.name}
                  </CardTitle>
                  <CardDescription className="text-gray-600">
                    {category.description}
                  </CardDescription>
                </CardHeader>
                <CardContent className="text-center">
                  <div className="flex items-center justify-between mb-4">
                    <Badge variant="secondary" className="text-sm">
                      {category.product_count} Products
                    </Badge>
                    <div className="flex items-center space-x-1">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className="h-4 w-4 fill-current text-yellow-400" />
                      ))}
                    </div>
                  </div>
                  <Button 
                    variant="outline" 
                    className="w-full group-hover:bg-automats-red group-hover:text-white group-hover:border-automats-red transition-all"
                    onClick={() => handleViewProducts(category.category_id, category.name)}
                  >
                    View Products
                    <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Enhanced Featured Product Highlight */}
        <div className="bg-white rounded-3xl p-8 lg:p-12 shadow-2xl border border-gray-100">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="space-y-4">
                <Badge className="bg-automats-eco-green/10 text-automats-eco-green border-automats-eco-green/20">
                  Featured Solution
                </Badge>
                <h3 className="text-4xl font-heading font-bold text-automats-black leading-tight">
                  All-Weather 5400 Series
                </h3>
                <div className="text-2xl font-heading font-semibold text-automats-red">
                  Premium Protection
                </div>
              </div>
              
              <p className="text-lg font-body text-automats-steel leading-relaxed">
                Our flagship all-weather mat combines deep groove channels with raised edges 
                to trap mud, water, and snow. Features our exclusive Griplock backing and 
                protective logo coating.
              </p>
              
              <div className="grid grid-cols-2 gap-6">
                <div className="bg-automats-off-white p-4 rounded-xl text-center">
                  <div className="text-3xl font-heading font-bold text-automats-eco-green mb-2">100%</div>
                  <div className="text-sm font-body text-automats-steel">Recycled Materials</div>
                </div>
                <div className="bg-automats-off-white p-4 rounded-xl text-center">
                  <div className="text-3xl font-heading font-bold text-automats-red mb-2">2-4</div>
                  <div className="text-sm font-body text-automats-steel">Piece Options</div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <a 
                  href="mailto:sales@automats.us?subject=Quote Request - All-Weather 5400 Series&body=Hello, I would like to request a quote for the All-Weather 5400 Series floor mats."
                  className="bg-automats-red hover:bg-red-700 text-white px-8 py-4 rounded-xl font-body font-semibold transition-all duration-300 hover:scale-105 text-center shadow-lg"
                >
                  Request Quote
                </a>
                <button 
                  onClick={() => navigate('/products')}
                  className="border-2 border-automats-steel text-automats-steel hover:bg-automats-steel hover:!text-white hover:border-automats-steel px-8 py-4 rounded-xl font-body font-semibold transition-all duration-300 hover:scale-105 text-center"
                >
                  View All Products
                </button>
              </div>
            </div>

            {/* Clean Product Visual */}
            <div 
              className="rounded-2xl p-12 aspect-square flex items-center justify-center relative"
              style={{
                background: `
                  linear-gradient(to top,
                    rgba(0,0,0,.4) 0%,
                    rgba(0,0,0,.2) 40%,
                    rgba(0,0,0,.1) 100%),
                  url('/images/overview-allweather5400.webp')
                `,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat'
              }}
            >
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProductOverview;